<template>
	<v-navigation-drawer permanent location="right" :order="1" :rail="mini" rail-width="48" width="380" mobile-breakpoint="0"
		color="panel_sub" theme="dark">
		<!-- 最小化時: 展開ボタンのみ -->
		<div v-if="mini" class="d-flex flex-column align-center pt-2">
			<v-btn icon variant="text" size="small" title="仮設道路AI チャット（試用版）" @click="mini = false">
				<v-badge content="試用版" location="bottom center" class="trial-badge">
					<v-icon>mdi-message-text</v-icon>
				</v-badge>
			</v-btn>
		</div>
		<!-- 展開時: チャット本体 -->
		<div v-else class="panel-main chat-panel-padding">
			<div class="d-flex justify-space-between align-center mb-1">
				<div class="d-flex align-center">
					<span class="text-subtitle-1">仮設道路AI チャット</span>
					<v-chip size="x-small" variant="flat" label class="ml-2 trial-chip">試用版</v-chip>
				</div>
				<v-btn icon variant="text" size="small" @click="mini = true">
					<v-icon>mdi-chevron-right</v-icon>
				</v-btn>
			</div>
			<div class="chat-messages-scroll" ref="messagesScroll">
				<!-- 試用版の注意書きと機能案内: 会話履歴（store）に含めず常に先頭へ固定表示する -->
				<div class="chat-message ai-message trial-notice" style="white-space:pre-wrap; word-wrap:break-word;">
					{{ trialNotice }}
				</div>
				<div class="chat-message ai-message" style="white-space:pre-wrap; word-wrap:break-word;">
					{{ introMessage }}
				</div>
				<div v-for="(msg, idx) in chatContext" :key="idx"
					:class="['chat-message', msg.role === 'user' ? 'user-message' : 'ai-message']"
					style="white-space:pre-wrap; word-wrap:break-word;">
					{{ msg.text }}
				</div>
			</div>
			<div class="chat-input-area">
				<div v-if="isRunning" class="text-caption mb-1">計算中です...</div>
				<v-textarea v-model="inputText" label="メッセージを入力" :counter="300" :maxlength="300" rows="3" max-rows="3" auto-grow
					class="mt-4" @keydown.enter="onEnter" :disabled="isRunning" />
			</div>
		</div>
	</v-navigation-drawer>
</template>

<script>
import "@/assets/icons/style.css";
import config from "@/config.json";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import * as roadAIUtil from "../../utils/roadAIUtils";
import { isImeComposingKeyEvent } from "@/utils/imeComposition";
import { useRoadAi } from "./road-ai/useRoadAi";
dayjs.extend(isSameOrAfter);

export default {
	name: "AiChatbot",
	components: {},
	created() {
		// 多段ワークフロー（検査 → 実行 → 保存 → サマリ生成）は feature facade に委譲する。
		this.roadAi = useRoadAi(this.$store);
	},
	unmounted() {
		this.roadAi.clearConversation();
	},
	watch: {
		chatContext: {
			handler() {
				this.$nextTick(() => this.scrollToBottom());
			},
			deep: true,
		},
	},
	data: () => ({
		config,
		mini: true,
		inputText: "",
		introMessage:
			"仮設道路を作成するためのチャットです。\n「PC200の仮設道路を作りたい」のように作りたい道路を入力してください。始点・終点はビュー上で指定し、通行する建機や重視したい条件（切土を減らしたい等）を伺ったうえで道路を作成します。",
		trialNotice:
			"本機能は試用版です。\n生成される仮設道路や応答内容が期待どおりにならない場合があります。結果は必ずご確認のうえご利用ください。",
	}),
	computed: {
		chatContext() {
			return this.$store.getters["addRoadTerrainByAi/chatContext"];
		},
		postChatContext() {
			return this.$store.getters["addRoadTerrainByAi/postChatContext"];
		},
		isRunning() {
			return this.$store.getters["addRoadTerrainByAi/isRunning"];
		},
	},
	methods: {
		onEnter(e) {
			// Let the IME confirm the composition; do not preventDefault
			if (isImeComposingKeyEvent(e)) return;
			if (e.shiftKey) {
				// Shift + Enterで改行
				return;
			}
			e.preventDefault();
			this.sendMessage();
		},
		async sendMessage() {
			if (this.inputText.trim() === "") return;
			this.$store.dispatch("addRoadTerrainByAi/add_chat_message", {
				role: "user",
				text: this.inputText,
			});
			this.$store.dispatch("addRoadTerrainByAi/add_post_chat_message", {
				role: "user",
				text: this.inputText,
			});
			this.$nextTick(this.scrollToBottom);
			this.inputText = "";
			try {
				const response = await roadAIUtil.executeChatbot(this.$store);
				this.roadAi.pushAssistant(response.response);
				// 新方式（D3D-774）: 会話から抽出したパラメータ・点群のみ指定を保存する（AC-35）
				this.roadAi.applyChatbotResponse(response);
				if (response.is_start_end_point) {
					// 始終点の設定: 表示中の点群・設計を検査し、点選択を起動する
					await this.startPointSelection();
				} else if (response.is_create_road_ai) {
					// 新方式で実行する
					await this.runRoadAI();
				}
			} catch (error) {
				console.error("Error sending message:", error);
				this.$store.dispatch("addRoadTerrainByAi/add_chat_message", {
					role: "assistant",
					text: "メッセージの送信中にエラーが発生しました。リロードしてもう一度お試しください。",
				});
			}
		},
		// 新方式（D3D-774）: 始終点選択を起動する（facade へ委譲）
		startPointSelection() {
			return this.roadAi.startPointSelection();
		},
		// 新方式（D3D-774）: 会話条件が揃ったら経路生成を実行する（facade へ委譲）
		runRoadAI() {
			return this.roadAi.runRoadAi(this.$route.query.siteId);
		},
		clearChat() {
			this.roadAi.clearConversation();
			this.$store.dispatch("addRoadTerrainByAi/reset_ai_parameter"); // パラメータのリセット
			this.$emit("road-ai-creation"); // 始点終点の再設定
		},
		scrollToBottom() {
			const el = this.$refs.messagesScroll;
			if (el) {
				el.scrollTop = el.scrollHeight;
			}
		},
		closeAiChatbot() {
			this.roadAi.clearConversation();
			this.$emit("close-ai-chatbot");
		},
	},
};
</script>

<style scoped>

.panel-main {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.panel-main-item {
  height: 32px;
  padding: 0;
  margin-bottom: 8px;
}

.add-btn {
  justify-content: space-between;
  width: 250px;
  height: 100%;
  margin: auto;
  padding: 4px 16px;
}

.globe-switch {
  position: absolute;
  right: 10px;
}

.chat-message.ai-message {
  text-align: left;
  background: #222c3a;
  color: #fff;
  border: 1px solid #bbb;
  padding: 8px 12px;
  margin: 8px 0;
  border-radius: 8px;
  max-width: 80%;
}

.chat-message.trial-notice {
  border-color: rgb(var(--v-theme-warning));
}

/* 試用版表示: 暗い背景 + 白文字でコントラストを確保し、オレンジ枠で目立たせる */
.trial-badge :deep(.v-badge__badge),
.trial-chip {
  background: #6b3300 !important;
  color: #fff !important;
  border: 1px solid rgb(var(--v-theme-warning));
}

/* 48px のレール幅に収まるよう試用版バッジを小さくする */
.trial-badge :deep(.v-badge__badge) {
  font-size: 9px;
  height: 14px;
  min-width: 0;
  padding: 0 3px;
}

.chat-message.user-message {
  text-align: left;
  background: #1976d2;
  color: #fff;
  padding: 8px 12px;
  margin: 8px 0 8px auto;
  border-radius: 8px;
	max-width: 80%;
}

.content-area {
  height: calc(100% - 36px) !important;
}

/* 入力欄やボタンに余白をつける */
.chat-panel-padding {
  padding: 16px 20px 20px 20px;
  box-sizing: border-box;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chat-messages-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

.chat-input-area {
  flex-shrink: 0;
  padding-top: 8px;
  background: inherit;
}
</style>
